import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnersApi, Partner, PartnerPlacement, CreatePartnerPayload, UpdatePartnerPayload } from '../../api/partners';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Plus, BarChart3, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface PartnersPageProps {
  isDark: boolean;
}

const PLACEMENTS: PartnerPlacement[] = ['landing', 'dashboard', 'footer', 'all'];

interface FormState {
  slug: string;
  name: string;
  description: string;
  url: string;
  logo_url: string;
  cta_label: string;
  placement: PartnerPlacement;
  is_active: boolean;
}

const emptyForm: FormState = {
  slug: '',
  name: '',
  description: '',
  url: '',
  logo_url: '',
  cta_label: '',
  placement: 'all',
  is_active: true,
};

export default function PartnersPage({ isDark }: PartnersPageProps) {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await partnersApi.list();
      setPartners(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch partners');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: Partner) => {
    setEditingId(p.id);
    setForm({
      slug: p.slug,
      name: p.name,
      description: p.description ?? '',
      url: p.url,
      logo_url: p.logo_url ?? '',
      cta_label: p.cta_label ?? '',
      placement: p.placement,
      is_active: p.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    if (!editingId && !form.slug.trim()) {
      toast.error('Slug is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        const payload: UpdatePartnerPayload = {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          url: form.url.trim(),
          logo_url: form.logo_url.trim() || undefined,
          cta_label: form.cta_label.trim() || undefined,
          placement: form.placement,
          is_active: form.is_active,
        };
        const updated = await partnersApi.update(editingId, payload);
        setPartners((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast.success('Partner updated');
      } else {
        const payload: CreatePartnerPayload = {
          slug: form.slug.trim(),
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          url: form.url.trim(),
          logo_url: form.logo_url.trim() || undefined,
          cta_label: form.cta_label.trim() || undefined,
          placement: form.placement,
          is_active: form.is_active,
        };
        const created = await partnersApi.create(payload);
        setPartners((prev) => [created, ...prev]);
        toast.success('Partner created');
      }
      setDialogOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save partner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (p: Partner, value: boolean) => {
    try {
      const updated = await partnersApi.update(p.id, { is_active: value });
      setPartners((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update');
    }
  };

  const handleDelete = async (p: Partner) => {
    if (!window.confirm(`Delete partner "${p.name}"? Click history will be removed too.`)) return;
    try {
      await partnersApi.remove(p.id);
      setPartners((prev) => prev.filter((x) => x.id !== p.id));
      toast.success('Partner deleted');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const placementBadge = (placement: PartnerPlacement) => {
    const colors: Record<PartnerPlacement, string> = {
      landing: 'bg-blue-500',
      dashboard: 'bg-emerald-500',
      footer: 'bg-purple-500',
      all: 'bg-slate-500',
    };
    return <Badge className={cn('text-white capitalize', colors[placement])}>{placement}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>
            Partners
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Manage sponsored banners shown on the public website
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
          <Plus className="w-4 h-4 mr-2" />
          New Partner
        </Button>
      </div>

      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center p-12">
            <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
              No partners yet. Click "New Partner" to add the first one.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Placement</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs">{p.slug}</TableCell>
                  <TableCell>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#0EA5A4] hover:underline text-sm max-w-[260px] truncate"
                    >
                      <span className="truncate">{p.url}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </TableCell>
                  <TableCell>{placementBadge(p.placement)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={p.is_active}
                      onCheckedChange={(value) => handleToggleActive(p, value)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/partners/${p.id}/stats`)}
                        title="Statistics"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(p)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={cn('max-w-2xl', isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white')}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
              {editingId ? 'Edit Partner' : 'New Partner'}
            </DialogTitle>
            <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
              {editingId ? 'Update the partner banner content' : 'Create a sponsored partner banner'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-2 md:col-span-1">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Slug *</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="students_app"
                disabled={!!editingId}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Talaba+"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>URL *</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://www.students-app.uz"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Talabalar uchun ekosistema..."
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Logo URL</Label>
              <Input
                value={form.logo_url}
                onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                placeholder="https://.../logo.png"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>CTA label</Label>
              <Input
                value={form.cta_label}
                onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
                placeholder="Saytga o'tish"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Placement</Label>
              <Select
                value={form.placement}
                onValueChange={(value) => setForm((f) => ({ ...f, placement: value as PartnerPlacement }))}
              >
                <SelectTrigger className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLACEMENTS.map((p) => (
                    <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm((f) => ({ ...f, is_active: checked }))}
              />
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
